import { useState, useEffect, useCallback, useId } from "react";
import { configuration } from "../Configuration";
import { authenticatedFetch } from "../JWTAuthService";

import { ChevronRight, Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

/**
 * LLM forensics viewer (System-Admin). Lists the 10 most recent conversations across all customers
 * and, for a chosen one, renders one collapsible timeline that interleaves (by timestamp) the
 * conversation's messages (user prompt / final response), every LLM step, and every tool invocation
 * with its input parameters — plus the conversation's data pool. Every row is collapsed by default.
 * Source: GET /admin/conversations/:id/llm-trace → { records, messages, dataPool }.
 */

interface LlmTokens {
  input: number;
  output: number;
  total: number;
  cached?: number;
  source: string;
}

interface TraceRecord {
  _id?: string;
  kind?: "llm" | "tool";
  seq: number;
  timestamp: string;
  phase?: string;
  nodeId?: string;
  toolName?: string;
  // llm
  provider?: string;
  model?: string;
  temperature?: number;
  prompt?: string;
  response?: string;
  finishReason?: string;
  latencyMs?: number;
  tokens?: LlmTokens;
  retries?: number;
  error?: string;
  truncated?: boolean;
  // tool
  parameters?: string;
}

interface ConversationMessage {
  type?: string; // 'userMessage' | 'botMessage'
  content?: string;
  date?: string;
  user?: string;
}

interface RecentConversation {
  _id: string;
  customer?: string;
  user?: string;
  topic?: string;
  created?: string;
  messageCount?: number;
  feedback?: { good: number; bad: number };
  latestFeedback?: string;
}

type ConversationFilter = "recent" | "good" | "bad";

interface DataPoolItem {
  id?: string;
  name?: string;
  description?: string;
  source?: string;
  node?: string;
  data?: unknown;
  timestamp?: string;
}

type TimelineItem =
  | { key: string; time: number; order: number; kind: "message"; msg: ConversationMessage }
  | { key: string; time: number; order: number; kind: "record"; rec: TraceRecord };

/** Codeblock der Detailansicht (Prompt, Antwort, Parameter, Pool-Daten). */
const PRE_CLASS =
  "my-1 mb-3 max-h-[360px] min-w-0 overflow-auto whitespace-pre-wrap break-words rounded-md border bg-muted p-2.5 text-xs";

interface Badge {
  label: string;
  className: string;
}

/**
 * Kategoriefarben der Zeitleiste. Die Kategorien sind gleichrangig (kein
 * Gut/Schlecht), daher eine kategoriale Palette statt Status-Tokens; die
 * Paare sind auf AA-Kontrast geprüft.
 */
const phaseBadge = (r: TraceRecord): Badge => {
  if (r.kind === "tool") return { label: "Tool", className: "bg-orange-100 text-orange-900" };
  switch (r.phase) {
    case "tool":
      return { label: "LLM · tool", className: "bg-blue-100 text-blue-900" };
    case "routing":
      return { label: "Routing", className: "bg-purple-100 text-purple-900" };
    case "synthesis":
      return { label: "Synthese", className: "bg-emerald-100 text-emerald-900" };
    default:
      return { label: `LLM · ${r.phase || "turn"}`, className: "bg-muted text-muted-foreground" };
  }
};

const truncate = (s: string, n: number) => (s.length > n ? s.slice(0, n) + "…" : s);

const feedbackBadge = (f?: { good: number; bad: number }) => {
  if (!f || (!f.good && !f.bad)) return <span className="text-muted-foreground">—</span>;
  return (
    <span>
      {f.good > 0 && <span className="text-success">👍 {f.good}</span>}
      {f.good > 0 && f.bad > 0 && " "}
      {f.bad > 0 && <span className="text-destructive">👎 {f.bad}</span>}
    </span>
  );
};

/** Merge messages + records into one chronological timeline (all carry timestamps). */
function buildTimeline(messages: ConversationMessage[], records: TraceRecord[]): TimelineItem[] {
  let last = 0;
  const msgItems: TimelineItem[] = messages.map((m, i) => {
    const parsed = m.date ? new Date(m.date).getTime() : NaN;
    const time = Number.isFinite(parsed) ? parsed : last;
    if (Number.isFinite(parsed)) last = parsed;
    return { key: `m-${i}`, time, order: i, kind: "message", msg: m };
  });
  const recItems: TimelineItem[] = records.map((r, i) => ({
    key: `r-${r.kind || "llm"}-${r.seq}-${i}`,
    time: r.timestamp ? new Date(r.timestamp).getTime() : 0,
    order: r.seq,
    kind: "record",
    rec: r,
  }));
  return [...msgItems, ...recItems].sort(
    (a, b) =>
      a.time - b.time ||
      (a.kind === b.kind ? a.order - b.order : a.kind === "message" ? -1 : 1),
  );
}

/** Aufklappbare Zeile der Zeitleiste: Kopf als Schaltfläche, Rumpf als Bereich. */
function Row({
  itemKey,
  open,
  onToggle,
  badge,
  primary,
  meta,
  body,
}: {
  itemKey: string;
  open: boolean;
  onToggle: () => void;
  badge: Badge;
  primary: React.ReactNode;
  meta: React.ReactNode;
  body: React.ReactNode;
}) {
  const bodyId = `llm-forensics-body-${itemKey}`;

  return (
    <div className="border-b last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={bodyId}
        className={cn(
          "flex w-full min-w-0 flex-wrap items-center gap-2 px-2.5 py-2 text-left hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          open && "bg-muted/50",
        )}
      >
        <ChevronRight
          aria-hidden
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-90",
          )}
        />
        <span
          className={cn(
            "shrink-0 truncate rounded px-1.5 py-0.5 text-xs font-bold",
            badge.className,
          )}
        >
          {badge.label}
        </span>
        <span className="min-w-0 flex-1 truncate text-sm text-foreground">{primary}</span>
        <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground">{meta}</span>
      </button>
      {open && (
        <div id={bodyId} className="min-w-0 bg-muted/30 px-3.5 pb-0.5 pt-1.5">
          {body}
        </div>
      )}
    </div>
  );
}

export default function LlmForensicsPanel() {
  const fieldId = useId();
  const [conversationId, setConversationId] = useState("");
  const [records, setRecords] = useState<TraceRecord[] | null>(null);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [dataPool, setDataPool] = useState<DataPoolItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [dpOpen, setDpOpen] = useState(false);

  const [recent, setRecent] = useState<RecentConversation[]>([]);
  const [recentError, setRecentError] = useState<string | null>(null);
  const [recentLoading, setRecentLoading] = useState(false);
  const [filter, setFilter] = useState<ConversationFilter>("recent");
  const [activeId, setActiveId] = useState<string>("");

  const conversationIdFieldId = `${fieldId}-conversation-id`;

  const load = useCallback(async (idArg?: string) => {
    const id = (idArg ?? "").trim() || conversationId.trim();
    if (!id) return;
    setConversationId(id);
    setActiveId(id);
    setLoading(true);
    setError(null);
    setRecords(null);
    setMessages([]);
    setDataPool([]);
    setExpanded({});
    setDpOpen(false);
    try {
      const response = await authenticatedFetch(
        `${configuration.serverUrl}/admin/conversations/${encodeURIComponent(id)}/llm-trace`,
        { method: "GET", headers: { "Content-Type": "application/json" } },
      );
      if (!response.ok) throw new Error(`Serverfehler ${response.status}`);
      const data = await response.json();
      setRecords(Array.isArray(data.records) ? data.records : []);
      setMessages(Array.isArray(data.messages) ? data.messages : []);
      setDataPool(Array.isArray(data.dataPool) ? data.dataPool : []);
    } catch (e: any) {
      setError(e?.message || "Fehler beim Laden der Forensik-Daten");
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    (async () => {
      setRecentLoading(true);
      setRecentError(null);
      const url =
        filter === "recent"
          ? `${configuration.serverUrl}/admin/llm-trace/recent-conversations`
          : `${configuration.serverUrl}/admin/llm-trace/flagged-conversations?flag=${filter}`;
      try {
        const response = await authenticatedFetch(url, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
        if (!response.ok) throw new Error(`Serverfehler ${response.status}`);
        const data = await response.json();
        setRecent(Array.isArray(data.conversations) ? data.conversations : []);
      } catch (e: any) {
        setRecent([]);
        setRecentError(e?.message || "Fehler beim Laden der Konversationen");
      } finally {
        setRecentLoading(false);
      }
    })();
  }, [filter]);

  const toggle = (key: string) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const llmRecords = (records || []).filter((r) => (r.kind || "llm") === "llm");
  const toolRecords = (records || []).filter((r) => r.kind === "tool");
  const totals = llmRecords.reduce(
    (acc, r) => {
      acc.input += r.tokens?.input || 0;
      acc.output += r.tokens?.output || 0;
      acc.total += r.tokens?.total || 0;
      acc.latency += r.latencyMs || 0;
      return acc;
    },
    { input: 0, output: 0, total: 0, latency: 0 },
  );

  const timeline = records !== null ? buildTimeline(messages, records) : [];

  const renderMessage = (m: ConversationMessage, key: string) => {
    const isUser = m.type === "userMessage";
    return (
      <Row
        key={key}
        itemKey={key}
        open={!!expanded[key]}
        onToggle={() => toggle(key)}
        badge={
          isUser
            ? { label: "Nutzer", className: "bg-blue-100 text-blue-900" }
            : { label: "Antwort", className: "bg-emerald-100 text-emerald-900" }
        }
        primary={m.content ? truncate(m.content.replace(/\s+/g, " "), 120) : <span className="text-muted-foreground">(leer)</span>}
        meta={m.date ? new Date(m.date).toLocaleTimeString() : ""}
        body={<pre className={PRE_CLASS}>{m.content || "(leer)"}</pre>}
      />
    );
  };

  const renderRecord = (r: TraceRecord, key: string) => {
    if (r.kind === "tool") {
      return (
        <Row
          key={key}
          itemKey={key}
          open={!!expanded[key]}
          onToggle={() => toggle(key)}
          badge={phaseBadge(r)}
          primary={<><strong>{r.toolName || "?"}</strong>{r.truncated ? <span className="text-warning"> · gekürzt</span> : null}</>}
          meta={r.timestamp ? new Date(r.timestamp).toLocaleTimeString() : ""}
          body={
            <>
              <div className="text-xs font-semibold text-foreground">Eingabeparameter{r.nodeId ? ` · node ${r.nodeId}` : ""}</div>
              <pre className={PRE_CLASS}>{r.parameters || "(keine)"}</pre>
            </>
          }
        />
      );
    }
    return (
      <Row
        key={key}
        itemKey={key}
        open={!!expanded[key]}
        onToggle={() => toggle(key)}
        badge={phaseBadge(r)}
        primary={
          <>
            <strong>{r.model}</strong>
            {r.toolName ? <span className="text-muted-foreground"> · {r.toolName}</span> : null}
            {r.error ? <span className="text-destructive"> · Fehler</span> : null}
            {r.truncated ? <span className="text-warning"> · gekürzt</span> : null}
          </>
        }
        meta={
          <>
            {r.tokens?.total?.toLocaleString() ?? "—"} tok · {r.latencyMs ?? "—"} ms ·{" "}
            {r.timestamp ? new Date(r.timestamp).toLocaleTimeString() : ""}
          </>
        }
        body={
          <>
            <div className="mb-1.5 text-xs text-muted-foreground">
              {r.provider} · in {r.tokens?.input ?? "—"} / out {r.tokens?.output ?? "—"}
              {typeof r.tokens?.cached === "number" ? ` / cached ${r.tokens.cached}` : ""} ·{" "}
              {r.tokens?.source === "usageMetadata" ? "exakte Tokens" : "geschätzte Tokens"}
              {typeof r.temperature === "number" ? ` · temp ${r.temperature}` : ""}
              {r.finishReason ? ` · finish ${r.finishReason}` : ""}
              {typeof r.retries === "number" && r.retries > 0 ? ` · ${r.retries} Retries` : ""}
              {r.nodeId ? ` · node ${r.nodeId}` : ""}
            </div>
            {r.error && <div className="mb-2 text-xs text-destructive">Fehler: {r.error}</div>}
            <div className="text-xs font-semibold text-foreground">Prompt</div>
            <pre className={PRE_CLASS}>{r.prompt}</pre>
            <div className="text-xs font-semibold text-foreground">Antwort</div>
            <pre className={PRE_CLASS}>{r.response}</pre>
          </>
        }
      />
    );
  };

  return (
    <div className="flex w-full min-w-0 max-w-[1000px] flex-col gap-4">
      <div>
        <h2 className="text-lg font-semibold">LLM-Forensik</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Nachrichten, LLM-Schritte und Tool-Aufrufe einer Konversation — mit Prompts, Antworten,
          Parametern, Tokens, Laufzeit und Datenpool.
        </p>
      </div>

      <div className="flex min-w-0 flex-wrap items-end gap-2">
        <div className="flex min-w-[200px] flex-1 flex-col gap-1.5">
          <Label htmlFor={conversationIdFieldId}>Conversation-ID</Label>
          <Input
            id={conversationIdFieldId}
            type="text"
            value={conversationId}
            onChange={(e) => setConversationId(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="z. B. 665f1c2a9b4d…"
          />
        </div>
        <Button
          className="max-sm:w-full"
          onClick={() => load()}
          disabled={loading || !conversationId.trim()}
        >
          {loading && <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />}
          {loading ? "Lädt…" : "Laden"}
        </Button>
      </div>

      {/* Conversation list across all customers — clickable quick-pick, with feedback filter. */}
      <div className="flex min-w-0 flex-col gap-1.5">
        <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
            {filter === "recent" ? "Letzte Konversationen (alle Kunden)" : `Bewertet: ${filter === "good" ? "👍 gut" : "👎 schlecht"} (letzte 30 Tage)`}
            {recentLoading && <Loader2 aria-hidden className="h-4 w-4 animate-spin text-muted-foreground" />}
          </h3>
          <Tabs value={filter} onValueChange={(value) => setFilter(value as ConversationFilter)}>
            <TabsList className="max-w-full overflow-x-auto">
              <TabsTrigger value="recent">Aktuell</TabsTrigger>
              <TabsTrigger value="good">👍 Gut (30T)</TabsTrigger>
              <TabsTrigger value="bad">👎 Schlecht (30T)</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {recentError && (
          <Alert variant="destructive">
            <AlertDescription className="min-w-0 break-words">{recentError}</AlertDescription>
          </Alert>
        )}
        {recentLoading && recent.length === 0 && (
          <div className="flex items-center gap-2 py-1.5 text-sm text-muted-foreground" role="status">
            <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> Konversationen werden geladen…
          </div>
        )}
        {!recentLoading && !recentError && recent.length === 0 && (
          <div className="rounded-md border py-6 text-center text-sm text-muted-foreground">
            {filter === "recent" ? "Keine Konversationen gefunden." : "Keine bewerteten Konversationen in den letzten 30 Tagen."}
          </div>
        )}
        {recent.length > 0 && (
          <ul
            className={cn(
              "min-w-0 overflow-hidden rounded-md border",
              recentLoading && "opacity-60",
            )}
          >
            {recent.map((c) => (
              <li key={c._id} className="border-b last:border-b-0">
                <button
                  type="button"
                  onClick={() => load(c._id)}
                  title={c._id}
                  aria-current={activeId === c._id ? "true" : undefined}
                  className={cn(
                    "grid w-full min-w-0 grid-cols-1 items-center gap-x-2 px-2.5 py-1.5 text-left hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring sm:grid-cols-[minmax(0,1fr)_130px_130px_110px_55px_78px]",
                    activeId === c._id && "bg-accent",
                  )}
                >
                  <span className="min-w-0 truncate text-sm text-foreground">
                    {c.topic?.trim() || <span className="text-muted-foreground">(kein Thema)</span>}
                  </span>
                  <span className="min-w-0 truncate text-xs text-muted-foreground">{c.customer || "—"}</span>
                  <span className="min-w-0 truncate text-xs text-muted-foreground">{c.user || "—"}</span>
                  <span className="min-w-0 truncate text-xs text-muted-foreground">{c.created ? new Date(c.created).toLocaleString() : "—"}</span>
                  <span className="text-xs text-muted-foreground sm:text-right">{c.messageCount ?? 0} Msg</span>
                  <span className="whitespace-nowrap text-xs sm:text-right">{feedbackBadge(c.feedback)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription className="min-w-0 break-words">{error}</AlertDescription>
        </Alert>
      )}

      {records !== null && !error && (
        <>
          <div className="min-w-0 text-sm text-foreground">
            <strong>{messages.length}</strong> Nachrichten · <strong>{llmRecords.length}</strong> LLM-Schritte ·{" "}
            <strong>{toolRecords.length}</strong> Tool-Aufrufe · Tokens gesamt:{" "}
            <strong>{totals.total.toLocaleString()}</strong> ({totals.input.toLocaleString()} in / {totals.output.toLocaleString()} out) ·
            Laufzeit: <strong>{(totals.latency / 1000).toFixed(1)}s</strong>
          </div>

          {/* Data pool — always shown so "empty" is distinguishable from "missing"; collapsible when present. */}
          <div className="min-w-0 overflow-hidden rounded-md border">
            {dataPool.length > 0 ? (
              <button
                type="button"
                onClick={() => setDpOpen((v) => !v)}
                aria-expanded={dpOpen}
                aria-controls="llm-forensics-data-pool"
                className={cn(
                  "flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-semibold text-foreground hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                  dpOpen && "bg-muted/50",
                )}
              >
                <ChevronRight
                  aria-hidden
                  className={cn("h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform", dpOpen && "rotate-90")}
                />
                Datenpool ({dataPool.length} {dataPool.length === 1 ? "Eintrag" : "Einträge"})
              </button>
            ) : (
              <div className="px-3 py-2 text-sm font-semibold text-muted-foreground">
                Datenpool (0 Einträge)
                <span className="font-normal"> — für diese Konversation nicht vorhanden</span>
              </div>
            )}
            {dpOpen && (
              <div id="llm-forensics-data-pool" className="border-t">
                {dataPool.map((it, i) => {
                  const key = `dp-${i}`;
                  return (
                    <Row
                      key={key}
                      itemKey={key}
                      open={!!expanded[key]}
                      onToggle={() => toggle(key)}
                      badge={{ label: "Pool", className: "bg-indigo-100 text-indigo-900" }}
                      primary={<><strong>{it.name || it.id || `Eintrag ${i + 1}`}</strong>{it.description ? <span className="text-muted-foreground"> · {truncate(it.description, 80)}</span> : null}</>}
                      meta={[it.source, it.node].filter(Boolean).join(" · ")}
                      body={<pre className={PRE_CLASS}>{typeof it.data === "string" ? it.data : JSON.stringify(it.data, null, 2)}</pre>}
                    />
                  );
                })}
              </div>
            )}
          </div>

          {records.length === 0 && messages.length === 0 && (
            <div className="rounded-md border py-6 text-center text-sm text-muted-foreground">
              Nichts für diese Konversation gefunden.
            </div>
          )}
          {records.length === 0 && messages.length > 0 && (
            <div className="text-xs text-muted-foreground">
              Keine LLM-Schritte aufgezeichnet (Konversation älter als die Forensik oder Aufzeichnung deaktiviert).
            </div>
          )}

          {timeline.length > 0 && (
            <div className="min-w-0 overflow-hidden rounded-md border">
              {timeline.map((item) =>
                item.kind === "message" ? renderMessage(item.msg, item.key) : renderRecord(item.rec, item.key),
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
